import { useEffect, useState, type MouseEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertIcon, ArrowDownIcon, ArrowUpIcon, Checkbox, CircleCheckIcon, DateTimePicker, ErrorState, LoadingState, Modal, MultiSelect, RefreshIcon, Select, Switch, relativeTime, toISODate, toLocalDate, useToast } from "@signet/ui";
import { folderUrl } from "../shared/downloads.ts";
import { countLabel, DEFAULT_COUNT, DEFAULT_DUPLICATES, MAX_DUPLICATES, MAX_REELS, MIN_MULTIPLIER, SORTS, type RowRun, type RunView, type Sort } from "../shared/redownload.ts";
import { confirmRedownload, fetchRedownload, redownloadKeys, startRedownload } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Re-Download on the page (Jay, 2026-10-21): the button on each creator's
   row and its popup. A run takes the creator's Reels posted between two days,
   sorted, the first so many or all, and duplicates them into one of the
   folders their flows already write to. The popup also shows how the last run
   is getting on.
   ────────────────────────────────────────────────────────────────────────── */

const stop = (event: MouseEvent) => event.stopPropagation();
const ALL = "__all";
const day = (d: Date) => toISODate(d);
const short = (iso: string) => toLocalDate(iso)!.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });

/**
 * The button on a creator's row follows their latest run (Ben, 2026-10-31): "Re-download in progress"
 * while it reads, downloads and duplicates, then "Completed" so he knows to go and check the Drive
 * folder, or "Re-download failed", until someone ticks the box beside it. The tick is stored, so the
 * row goes back to Re-Download for everyone. Each state still opens the popup, which says more.
 */
export function RedownloadButton({ creator, run, onOpen }: { creator: string; run: RowRun | null; onOpen: () => void }) {
  const client = useQueryClient();
  const toast = useToast();
  const confirm = useMutation({
    mutationFn: () => confirmRedownload(creator, run!.id),
    onSuccess: (r) => {
      client.setQueryData(redownloadKeys.rows, r);
      client.invalidateQueries({ queryKey: redownloadKeys.creator(creator) });
    },
    onError: (error: Error) => toast.error("Couldn't tick it as checked", error.message),
  });

  if (run?.state === "running") {
    return (
      <button type="button" className="ma-rd-state busy" title="Open to see how far it has got" onClick={(e) => (stop(e), onOpen())}>
        <span className="ma-rd-spin" aria-hidden="true" /> Re-download in progress
      </button>
    );
  }
  if (run) {
    const done = run.state === "done";
    const tip = done ? `Duplicated into ${run.destName}. Tick once you've checked the Drive folder.` : `${run.error ?? "It stopped before it finished."} Tick to clear it.`;
    return (
      <span className="ma-rd-finished" onClick={stop}>
        <button type="button" className={`ma-rd-state ${done ? "done" : "bad"}`} title={tip} onClick={onOpen}>
          {done ? <CircleCheckIcon size={13} /> : <AlertIcon size={13} />} {done ? "Completed" : "Re-download failed"}
        </button>
        <span className="ma-rd-confirm" title={tip}>
          <Checkbox checked={confirm.isPending} disabled={confirm.isPending} onChange={() => confirm.mutate()} label={done ? "Checked the Drive folder, back to Re-Download" : "Seen the failure, back to Re-Download"} />
        </span>
      </span>
    );
  }
  return (
    <button type="button" className="secondary-button ma-rd-button" onClick={(e) => (stop(e), onOpen())}>
      <RefreshIcon size={13} /> Re-Download
    </button>
  );
}

function DayPicker({ value, onChange, label, disabled }: { value: string; onChange: (day: string) => void; label: string; disabled: boolean }) {
  if (disabled) return <span className="ma-rd-static">{short(value)}</span>;
  return (
    <DateTimePicker
      label={label}
      value={value}
      withTime={false}
      compact
      clearable={false}
      className="ma-rd-day"
      renderValue={(iso) => (iso ? short(iso) : "Not set")}
      onChange={(iso) => iso && onChange(day(new Date(iso)))}
    />
  );
}

/**
 * With All ticked every Reel in the dates comes, so their order doesn't matter: the arrow greys out and
 * Sort is left with the one choice that still changes anything, every Reel or only the 1.5x ones
 * (Jay and Ben, 2026-10-27).
 */
const ALL_SORTS: { value: Sort; label: string }[] = [
  { value: "views", label: "Every Reel" },
  { value: "multiplier", label: `Multiplier, ${MIN_MULTIPLIER}x and up` },
];

const ORDER: Record<Sort, [string, string]> = { views: ["most viewed first", "least viewed first"], recent: ["newest first", "oldest first"], multiplier: ["highest multiplier first", "lowest multiplier first"] };

function runLine(run: RunView, lead = "Last run,"): string {
  const when = `${lead} ${relativeTime(run.requestedAt)}`;
  switch (run.state) {
    case "reading":
      return `${when}: reading Instagram, ${run.pages.toLocaleString()} ${run.pages === 1 ? "page" : "pages"} so far.`;
    case "downloading":
      return `${when}: downloading, ${run.sent.toLocaleString()} of ${run.picked.toLocaleString()} in Drive.`;
    case "duplicating":
      return `${when}: duplicating into ${run.destName}, ${run.left.toLocaleString()} left${run.stuck ? `, ${run.stuck.toLocaleString()} stuck on the Uploader queue` : ""}.`;
    case "done":
      return `${when}: ${countLabel(run.sent)} duplicated into ${run.destName}${run.failed ? `, ${run.failed.toLocaleString()} Instagram no longer serves` : ""}.`;
    default:
      return `${when}: failed. ${run.error ?? ""}`.trim();
  }
}

const running = (run: RunView | null | undefined) => Boolean(run && ["reading", "downloading", "duplicating"].includes(run.state));

/** "@a, @b", or "4 accounts" past two, as the accounts picker sums them up. */
const accountsLabel = (handles: string[]) => (handles.length <= 2 ? handles.map((h) => `@${h}`).join(", ") : `${handles.length} accounts`);

/** What a run was asked to do, in the words of the options window's preview line. */
function askedLine(run: RunView): string {
  const f = run.filters;
  const when = f.start && f.end ? `${short(f.start)} to ${short(f.end)}` : "any time";
  const which = `${countLabel(f.count)}${f.sort === "multiplier" ? ` at ${MIN_MULTIPLIER}x or more` : ""}`;
  const from = run.accounts.length ? ` from ${accountsLabel(run.accounts)}` : "";
  return `${which}${from}, ${when}${f.count === null ? "" : `, ${ORDER[f.sort][f.ascending ? 1 : 0]}`}. Each duplicated x${run.duplicates}, ${run.adjust ? "with" : "without"} adjustments.`;
}

/**
 * The window a row's In progress, Completed or failed button opens (Jay, 2026-11-02): the folder the copies
 * went to, what was done, and the tick. The options only come back once it is ticked and the row says
 * Re-Download again.
 */
function RunWindow({ creator, run, last, onClose }: { creator: string; run: RowRun; last: RunView | null; onClose: () => void }) {
  const client = useQueryClient();
  const toast = useToast();
  const confirm = useMutation({
    mutationFn: () => confirmRedownload(creator, run.id),
    onSuccess: (r) => {
      client.setQueryData(redownloadKeys.rows, r);
      client.invalidateQueries({ queryKey: redownloadKeys.creator(creator) });
      onClose();
    },
    onError: (error: Error) => toast.error("Couldn't tick it as checked", error.message),
  });
  const done = run.state === "done";
  const finished = run.state !== "running";
  // The row's run and the popup's latest are the same one unless another started in between.
  const view = last?.id === run.id ? last : null;
  return (
    <Modal
      open
      onClose={onClose}
      title={`Re-Download ${creator}`}
      description={done ? "Completed. Check the folder, then tick it off." : finished ? "This Re-Download failed." : "Still going. This updates by itself."}
      footer={
        <>
          {finished ? (
            <label className="ma-dl-check ma-rd-tick">
              <Checkbox checked={confirm.isPending} disabled={confirm.isPending} onChange={() => confirm.mutate()} label={done ? "Checked the Drive folder" : "Seen the failure"} />
              <span>{done ? "Checked the Drive folder" : "Seen the failure"}</span>
            </label>
          ) : null}
          <button type="button" className="text-button" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <div className="ma-rd-box">
        <div className="ma-rd-field">
          <span className="ma-rd-label">Folder</span>
          {view ? (
            <a className="ma-rd-folder" href={folderUrl(view.destId)} target="_blank" rel="noreferrer">
              {view.destName}
            </a>
          ) : (
            <span>{run.destName}</span>
          )}
        </div>
        {view ? (
          <>
            <div className="ma-rd-field">
              <span className="ma-rd-label">Asked for</span>
              <span className="ma-rd-row">{askedLine(view)}</span>
            </div>
            <p className={`ma-rd-preview${view.state === "failed" ? " bad" : ""}`}>{runLine(view, "Started")}</p>
          </>
        ) : run.error ? (
          <p className="ma-rd-preview bad">{run.error}</p>
        ) : null}
      </div>
    </Modal>
  );
}

export function RedownloadModal({ creator, run, open, onClose }: { creator: string; run: RowRun | null; open: boolean; onClose: () => void }) {
  const client = useQueryClient();
  const toast = useToast();
  const data = useQuery({
    queryKey: redownloadKeys.creator(creator),
    queryFn: () => fetchRedownload(creator),
    enabled: open,
    refetchInterval: (q) => (running(q.state.data?.last) ? 5_000 : false),
  });

  const today = new Date();
  const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
  // Empty is All accounts.
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [amount, setAmount] = useState(String(DEFAULT_COUNT));
  const [allPosts, setAllPosts] = useState(false);
  const [start, setStart] = useState(day(monthAgo));
  const [end, setEnd] = useState(day(today));
  const [allTime, setAllTime] = useState(false);
  const [sort, setSort] = useState<Sort>("views");
  const [ascending, setAscending] = useState(false);
  const [dest, setDest] = useState<string | null>(null);
  const [copies, setCopies] = useState(String(DEFAULT_DUPLICATES));
  const [adjust, setAdjust] = useState(true);

  // Back to the defaults each time the box opens, so a closed box never keeps a half-made run.
  useEffect(() => {
    if (!open) return;
    setAccountIds([]);
    setAmount(String(DEFAULT_COUNT));
    setAllPosts(false);
    setStart(day(monthAgo));
    setEnd(day(new Date()));
    setAllTime(false);
    setSort("views");
    setAscending(false);
    setDest(null);
    setCopies(String(DEFAULT_DUPLICATES));
    setAdjust(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const destinations = data.data?.destinations ?? [];
  const chosen = destinations.find((d) => d.folderId === dest) ?? null;
  const count = Number(amount);
  const countOk = allPosts || (Number.isInteger(count) && count >= 1 && count <= MAX_REELS);
  const datesOk = allTime || start <= end;
  const duplicates = Number(copies);
  const copiesOk = Number.isInteger(duplicates) && duplicates >= 1 && duplicates <= MAX_DUPLICATES;

  const go = useMutation({
    mutationFn: () => startRedownload(creator, { accountIds: accountIds.length ? accountIds : null, count: allPosts ? null : count, start: allTime ? null : start, end: allTime ? null : end, sort, ascending, destFolderId: chosen!.folderId, duplicates, adjust }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: redownloadKeys.all });
      toast.success("Re-Download started", `${creator}'s Reels are being read now, then duplicated into ${chosen!.name}.`);
      onClose();
    },
    onError: (error: Error) => toast.error("Couldn't start the Re-Download", error.message),
  });

  const accounts = data.data?.accounts ?? [];
  const accountOptions = [{ value: ALL, label: "All accounts" }, ...accounts.map((a) => ({ value: a.id, label: `@${a.handle}` }))];
  /**
   * Any few of the creator's accounts, or All (Ben, 2026-10-31: "it doesnt let u do lets say 3 accounts only lets you
   * do 1 or all"). Ticking All clears the others and ticking an account clears All; nothing ticked, or every account
   * ticked one by one, is All again, so the box never sits empty.
   */
  const pickAccounts = (next: string[]) => {
    const picked = !accountIds.length && next.includes(ALL) ? next.filter((v) => v !== ALL) : next.includes(ALL) ? [] : next;
    setAccountIds(picked.length === accounts.length ? [] : picked);
  };
  const accountSummary = (chosen: { label: string }[]) => (chosen.length <= 2 ? chosen.map((o) => o.label).join(", ") : `${chosen.length} accounts`);
  const when = allTime ? "any time" : `${short(start)} to ${short(end)}`;
  const which = `${countLabel(allPosts ? null : count)}${sort === "multiplier" ? ` at ${MIN_MULTIPLIER}x or more` : ""}`;
  const preview = chosen ? `${which}, ${when}${allPosts ? "" : `, ${ORDER[sort][ascending ? 1 : 0]}`}. Each duplicated x${copiesOk ? duplicates : "?"} into ${chosen.name}, ${adjust ? "with" : "without"} adjustments.` : null;

  if (open && run) return <RunWindow creator={creator} run={run} last={data.data?.last ?? null} onClose={onClose} />;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="wide"
      title={`Re-Download ${creator}`}
      description="Downloads Reels again into a temporary Drive folder, never RAW, and duplicates them into the folder you pick. The temporary folder goes to the bin once they are done."
      footer={
        <>
          <button type="button" className="text-button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button brass" disabled={!chosen || !countOk || !datesOk || !copiesOk || go.isPending} onClick={() => go.mutate()}>
            {go.isPending ? "Starting" : "Re-Download"}
          </button>
        </>
      }
    >
      {data.isLoading ? (
        <LoadingState />
      ) : data.isError || !data.data ? (
        <ErrorState title="Couldn't load Re-Download" onRetry={() => data.refetch()} />
      ) : (
        <div className="ma-rd-box">
          <div className="ma-rd-field">
            <span className="ma-rd-label">Accounts</span>
            <MultiSelect label="Which accounts" compact className="ma-rd-wide" values={accountIds.length ? accountIds : [ALL]} options={accountOptions} summary={accountSummary} onChange={pickAccounts} />
          </div>

          <div className="ma-rd-field">
            <span className="ma-rd-label">Number of posts</span>
            <div className="ma-rd-row">
              <input className="ma-rd-amount" inputMode="numeric" value={amount} disabled={allPosts} onChange={(e) => setAmount(e.target.value.replace(/\D/g, "").slice(0, 3))} aria-label="Number of posts" />
              <label className="ma-dl-check">
                <Checkbox
                  checked={allPosts}
                  onChange={(on) => {
                    setAllPosts(on);
                    if (on && sort === "recent") setSort("views");
                  }}
                  label="All posts"
                />
                <span>All</span>
              </label>
              {!countOk ? <span className="ma-rd-error">1 to {MAX_REELS} posts.</span> : null}
            </div>
          </div>

          <div className="ma-rd-field">
            <span className="ma-rd-label">Dates</span>
            <div className="ma-rd-row">
              <DayPicker label="Start date" value={start} onChange={setStart} disabled={allTime} />
              <span className={allTime ? "off" : ""}>to</span>
              <DayPicker label="End date" value={end} onChange={setEnd} disabled={allTime} />
              <label className="ma-dl-check">
                <Checkbox checked={allTime} onChange={setAllTime} label="All time" />
                <span>All time</span>
              </label>
              {!datesOk ? <span className="ma-rd-error">The start date is after the end date.</span> : null}
            </div>
          </div>

          <div className="ma-rd-field">
            <span className="ma-rd-label">Sort</span>
            <div className="ma-rd-row">
              <Select
                label="Sort by"
                compact
                searchable={false}
                className="ma-rd-sort"
                value={sort}
                options={allPosts ? ALL_SORTS : SORTS.map((s) => ({ value: s.id, label: s.label, ...(s.id === "multiplier" ? { hint: `${MIN_MULTIPLIER}x and up` } : {}) }))}
                onChange={(v) => v && setSort(v)}
              />
              <button
                type="button"
                className="icon-button ma-rd-arrow"
                aria-label={allPosts ? "Order doesn't matter with All" : ascending ? "Ascending, click for descending" : "Descending, click for ascending"}
                title={allPosts ? "Every Reel comes with All, so the order doesn't matter" : ascending ? "Ascending" : "Descending"}
                disabled={allPosts}
                onClick={() => setAscending(!ascending)}
              >
                {ascending ? <ArrowUpIcon size={14} /> : <ArrowDownIcon size={14} />}
              </button>
              {sort === "multiplier" ? <span className="ma-rd-hint">Only Reels at {MIN_MULTIPLIER}x or more. If fewer qualify, fewer come.</span> : null}
            </div>
          </div>

          <div className="ma-rd-field">
            <span className="ma-rd-label">Destination</span>
            {destinations.length ? (
              <Select label="Destination folder" compact className="ma-rd-wide" placeholder="Pick a folder" value={dest} options={destinations.map((d) => ({ value: d.folderId, label: d.name }))} onChange={(v) => setDest(v ?? null)} />
            ) : (
              <span className="ma-rd-hint">{creator} has no flows duplicating videos yet. Set them up on the Uploader page first.</span>
            )}
          </div>

          <div className="ma-rd-field">
            <span className="ma-rd-label">Duplicates</span>
            <div className="ma-rd-row">
              <input className="ma-rd-amount" inputMode="numeric" value={copies} onChange={(e) => setCopies(e.target.value.replace(/\D/g, "").slice(0, 2))} aria-label="Duplicates of each Reel" />
              {!copiesOk ? <span className="ma-rd-error">1 to {MAX_DUPLICATES} duplicates.</span> : null}
            </div>
          </div>

          <div className="ma-rd-field">
            <span className="ma-rd-label">Adjustments</span>
            <div className="ma-rd-row">
              <Switch checked={adjust} onChange={setAdjust} label="Adjustments" />
              <span className={adjust ? "" : "off"}>{adjust ? "On" : "Off"}</span>
            </div>
          </div>

          {preview ? <p className="ma-rd-preview">{preview}</p> : null}
          {data.data.last ? <p className={`ma-rd-last${data.data.last.state === "failed" ? " bad" : ""}`}>{runLine(data.data.last)}</p> : null}
        </div>
      )}
    </Modal>
  );
}
